import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle2, HelpCircle, Sparkles, Loader2, Pencil } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { usePanel } from '../contexts/PanelContext';
import { FieldInput, QuestionImpactInfo } from './SectionQuestionsTab';
import { InlineError } from './InlineError';
import { PageLoader } from './PageLoader';
import { generateMdx } from '../lib/markdown';
import type { FigureInput, OutstandingField, SummaryFigure, Task } from '../lib/types';
import { useAgentCard } from './AgentDock';
import { FIELD_SAVED_EVENT } from './FieldProposal';
import { AGENT_TURN_DONE_EVENT } from '../lib/agents';
import TaskCard, { isOpenTask } from './TaskCard';
import { FigureCard, InputCard, TargetCard, figureCell } from './FigureCard';
import { ConflictNotice, OriginLine } from './Provenance';

const optionLabels = (task: OutstandingField): unknown => {
    const label = (v: unknown) => task.options?.find(o => o.value === v)?.label ?? v;
    return Array.isArray(task.value) ? task.value.map(label) : label(task.value);
};

const display = (v: unknown): string => {
    if (v === true) return 'Yes';
    if (v === false) return 'No';
    if (Array.isArray(v)) return v.join(', ');
    if (v && typeof v === 'object') return JSON.stringify(v);
    return String(v ?? '');
};

function FieldCard({ task, onSaved, highlighted }: {
    task: OutstandingField; onSaved: () => void; highlighted?: boolean;
}) {
    const { authFetch } = useAuth();
    const { openPanel } = usePanel();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { startChat } = useAgentCard();
    const ref = useRef<HTMLDivElement>(null);
    const answered = task.status !== 'unknown';
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState<any>(answered ? task.value : '');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Arrived from a document's "answer this to see if it applies" link.
    useEffect(() => {
        if (highlighted) ref.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, [highlighted]);

    const save = async (v: any) => {
        setSaving(true);
        setError(null);
        try {
            const res = await authFetch(`/workspace/fields/${task.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ value: v }),
            });
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                throw new Error(body.detail || 'Could not save that answer');
            }
            setEditing(false);
            onSaved();
        } catch (e: any) {
            setError(e.message);
        } finally {
            setSaving(false);
        }
    };

    const useTheirs = async () => {
        const res = await authFetch(`/workspace/fields/${task.id}/use-source`, { method: 'POST' });
        if (res.ok) onSaved();
    };

    // Help runs in a fresh chat with the skill the template names, if any; the question goes in the
    // address first so the agent's view of the page names it.
    const draft = () => {
        navigate(`${pathname}?field=${encodeURIComponent(task.id)}`);
        startChat(task.draft?.skill ?? undefined, { ask: `Help me answer: ${task.label}`, beside: true });
    };

    // yes_no and single_choice commit on click; free text needs an explicit save.
    const immediate = task.input_type === 'yes_no' || task.input_type === 'single_choice';
    const asking = !answered || editing;

    return (
        <div ref={ref} className={`border rounded-xl p-4 transition-colors ${highlighted
            ? 'border-brand ring-2 ring-brand/20 bg-white'
            : answered && !editing
                ? 'border-softgrey/70 bg-offwhite/50'
                : 'border-softgrey bg-white'}`}>
            {asking ? (
                <FieldInput
                    field={task as any}
                    value={value}
                    onChange={(v) => { setValue(v); if (immediate) save(v); }}
                />
            ) : (
                <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-4 h-4 text-sage shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                        <p className="text-[13px] text-coolslate">{task.label}</p>
                        <p className="text-sm text-graphite mt-0.5 break-words">{display(optionLabels(task))}</p>
                        <p className="text-[11px] text-coolslate mt-1 flex items-center gap-1">
                            <OriginLine origin={task.origin} status={task.status} />
                        </p>
                    </div>
                </div>
            )}

            {!asking && (
                <ConflictNotice origin={task.origin} status={task.status}
                    theirs={display(task.origin?.conflict?.value)} onUseTheirs={useTheirs} />
            )}

            {error && <div className="mt-3"><InlineError message={error} /></div>}

            <div className="mt-3 flex flex-wrap items-center gap-3">
                {asking && !immediate && (
                    <button
                        onClick={() => save(value)}
                        disabled={saving || !String(value ?? '').trim()}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-medium rounded-lg bg-brand text-white hover:bg-brand-dark disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                        Save
                    </button>
                )}
                {asking && editing && (
                    <button
                        onClick={() => { setEditing(false); setValue(task.value); }}
                        className="text-[13px] font-medium text-coolslate hover:text-graphite transition-colors"
                    >
                        Cancel
                    </button>
                )}
                {answered && !editing && (
                    <button
                        onClick={() => setEditing(true)}
                        className="inline-flex items-center gap-1.5 text-[13px] font-medium text-brand hover:text-brand-dark transition-colors"
                    >
                        <Pencil className="w-3.5 h-3.5" />
                        {task.status === 'derived' ? 'Correct this' : 'Change'}
                    </button>
                )}
                {task.document_impact && task.document_impact.length > 0 && (
                    <QuestionImpactInfo impact={task.document_impact} />
                )}
                {task.guidance && (
                    <button
                        onClick={() => openPanel(
                            task.label,
                            <div
                                className="text-sm text-coolslate leading-relaxed"
                                dangerouslySetInnerHTML={{ __html: generateMdx(task.guidance!) }}
                            />
                        )}
                        className="inline-flex items-center gap-1.5 text-[13px] font-medium text-brand hover:text-brand-dark transition-colors"
                    >
                        <HelpCircle className="w-3.5 h-3.5" />
                        Show me how
                    </button>
                )}
                {task.draft && asking && (
                    <button
                        onClick={draft}
                        className="inline-flex items-center gap-1.5 text-[13px] font-medium text-brand hover:text-brand-dark transition-colors"
                    >
                        <Sparkles className="w-3.5 h-3.5" />
                        Help me do it
                    </button>
                )}
            </div>
        </div>
    );
}

// An area's fields, loaded once for its Summary and What's Next tabs.
export function useSectionFields(sectionId: string) {
    const { authFetch } = useAuth();
    const [fields, setFields] = useState<OutstandingField[] | null>(null);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(() => {
        authFetch(`/workspace/fields/outstanding?section_id=${encodeURIComponent(sectionId)}`)
            .then(r => r.ok ? r.json() : Promise.reject(new Error('Could not load the summary')))
            .then(setFields)
            .catch(e => { setError(e.message); setFields([]); });
    }, [authFetch, sectionId]);

    useEffect(load, [load]);

    // An answer picked in the chat is saved there, and an agent's turn can write the
    // record; show either here too.
    useEffect(() => {
        window.addEventListener(FIELD_SAVED_EVENT, load);
        window.addEventListener(AGENT_TURN_DONE_EVENT, load);
        return () => {
            window.removeEventListener(FIELD_SAVED_EVENT, load);
            window.removeEventListener(AGENT_TURN_DONE_EVENT, load);
        };
    }, [load]);

    return { fields, error, reload: load };
}

export const isToDetermine = (f: OutstandingField) => f.status === 'unknown';

interface FieldsTabProps {
    fields: OutstandingField[] | null;
    error: string | null;
    onSaved: () => void;
}

// The area's figures, how they stand against target, and what else is known about it.
export default function SectionSummaryTab({ fields, error, onSaved, figures, onFiguresSaved }: FieldsTabProps & {
    figures: SummaryFigure[] | null; onFiguresSaved: () => void;
}) {
    const { search } = useLocation();
    if (fields === null || figures === null) return <PageLoader label="Loading summary..." />;

    const focusField = new URLSearchParams(search).get('field');
    const known = fields.filter(f => f.status !== 'unknown');
    const shown = figures.filter(f => figureCell(f));
    const againstTarget = shown.filter(f => f.target);

    if (fields.length === 0 && figures.length === 0) {
        return (
            <div>
                {error && <InlineError message={error} />}
                <p className="text-sm text-coolslate py-6">
                    This area has no questions or figures that apply to your business.
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-8">
            {error && <InlineError message={error} />}

            {againstTarget.length > 0 && (
                <section>
                    <SummaryHeading title="Against Target" count={againstTarget.length} />
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        {againstTarget.map(f => <TargetCard key={f.id} figure={f} />)}
                    </div>
                </section>
            )}

            {shown.length > 0 && (
                <section>
                    <SummaryHeading title="Figures" count={shown.length} />
                    <div className="space-y-3">
                        {shown.map(f => <FigureCard key={f.id} figure={f} onSaved={onFiguresSaved} />)}
                    </div>
                </section>
            )}

            <section>
                <SummaryHeading title="What's Known" count={known.length} />
                {known.length === 0 ? (
                    <p className="text-[13px] text-coolslate">
                        Nothing recorded for this area yet.
                    </p>
                ) : (
                    <div className="space-y-3">
                        {known.map(f => <FieldCard key={f.id} task={f} onSaved={onSaved} highlighted={f.id === focusField} />)}
                    </div>
                )}
            </section>
        </div>
    );
}

// The area's next steps: its open tasks, what is still to determine, what the
// record needs for the figures it lacks, then the tasks already done.
export function SectionNextTab({ fields, error, onSaved, tasks, onTasksChanged, inputs, onInputSaved }: FieldsTabProps & {
    tasks: Task[] | null; onTasksChanged: () => void;
    inputs: FigureInput[] | null; onInputSaved: () => void;
}) {
    const { search } = useLocation();
    const [showDone, setShowDone] = useState(false);
    if (fields === null || tasks === null || inputs === null) return <PageLoader label="Loading..." />;

    const focusField = new URLSearchParams(search).get('field');
    const toDetermine = fields.filter(isToDetermine);
    const open = tasks.filter(isOpenTask);
    const done = tasks.filter(t => !isOpenTask(t));

    return (
        <div className="space-y-8">
            {error && <InlineError message={error} />}
            {open.length === 0 && toDetermine.length === 0 && inputs.length === 0 && (
                <p className="text-[13px] text-coolslate">Nothing left to do in this area.</p>
            )}

            {open.length > 0 && (
                <section>
                    <SummaryHeading title="To Do" count={open.length} />
                    <div className="space-y-3">
                        {open.map(t => <TaskCard key={t.id} task={t} onChanged={onTasksChanged} />)}
                    </div>
                </section>
            )}

            {toDetermine.length > 0 && (
                <section>
                    <SummaryHeading title="To Determine" count={toDetermine.length} />
                    <p className="text-[13px] text-coolslate mb-3">
                        Connecting a source often answers several of these at once.
                    </p>
                    <div className="space-y-3">
                        {toDetermine.map(f => <FieldCard key={f.id} task={f} onSaved={onSaved} highlighted={f.id === focusField} />)}
                    </div>
                </section>
            )}

            {inputs.length > 0 && (
                <section>
                    <SummaryHeading title="For Your Figures" count={inputs.length} />
                    <div className="space-y-3">
                        {inputs.map(i => <InputCard key={i.id} input={i} onSaved={onInputSaved} />)}
                    </div>
                </section>
            )}

            {done.length > 0 && (
                <section>
                    <button
                        onClick={() => setShowDone(v => !v)}
                        aria-expanded={showDone}
                        className="flex items-baseline gap-2 navaris-h3 mb-3 hover:text-brand-dark"
                    >
                        Done
                        <span className="text-[12px] font-medium text-coolslate">{done.length}</span>
                    </button>
                    {showDone && (
                        <div className="space-y-3">
                            {done.map(t => <TaskCard key={t.id} task={t} onChanged={onTasksChanged} />)}
                        </div>
                    )}
                </section>
            )}
        </div>
    );
}

function SummaryHeading({ title, count }: { title: string; count: number }) {
    return (
        <h4 className="flex items-baseline gap-2 navaris-h3 mb-3">
            {title}
            <span className="text-[12px] font-medium text-coolslate">{count}</span>
        </h4>
    );
}
